import { useCallback, useEffect, useRef, useState } from "react";
import { messagesApi } from "@/lib/apiClient";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function toWsUrl(ticket) {
    const base = BACKEND_URL.replace(/^http/, "ws");
    return `${base}/api/ws/messages?ticket=${encodeURIComponent(ticket)}`;
}

export function useMessagingRealtime(enabled, onEvent) {
    const wsRef = useRef(null);
    const retryRef = useRef(0);
    const timerRef = useRef(null);
    const onEventRef = useRef(onEvent);
    const [status, setStatus] = useState(enabled ? "connecting" : "idle");

    useEffect(() => {
        onEventRef.current = onEvent;
    }, [onEvent]);

    const clearRetry = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const connect = useCallback(async () => {
        if (!enabled) return;
        if (typeof window !== "undefined" && !window.navigator.onLine) {
            setStatus("offline");
            return;
        }
        clearRetry();
        setStatus(retryRef.current > 0 ? "reconnecting" : "connecting");
        try {
            const { ticket } = await messagesApi.wsTicket();
            const ws = new WebSocket(toWsUrl(ticket));
            wsRef.current = ws;
            ws.onopen = () => {
                retryRef.current = 0;
                setStatus("connected");
            };
            ws.onmessage = (event) => {
                try {
                    const payload = JSON.parse(event.data);
                    if (payload.type === "ws.ping") {
                        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "pong" }));
                        return;
                    }
                    onEventRef.current?.(payload);
                } catch (_) {}
            };
            ws.onerror = () => {
                setStatus("error");
            };
            ws.onclose = () => {
                if (!enabled) return;
                const nextAttempt = Math.min(retryRef.current + 1, 5);
                retryRef.current = nextAttempt;
                const delay = Math.min(1000 * (2 ** nextAttempt), 12000);
                setStatus(typeof window !== "undefined" && !window.navigator.onLine ? "offline" : "reconnecting");
                timerRef.current = setTimeout(() => connect(), delay);
            };
        } catch (_) {
            retryRef.current = Math.min(retryRef.current + 1, 5);
            setStatus("reconnecting");
            timerRef.current = setTimeout(() => connect(), Math.min(1000 * (2 ** retryRef.current), 12000));
        }
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return undefined;
        connect();
        const handleOnline = () => {
            retryRef.current = 0;
            connect();
        };
        const handleOffline = () => setStatus("offline");
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);
        return () => {
            clearRetry();
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            try { wsRef.current?.close(); } catch (_) {}
        };
    }, [enabled, connect]);

    const send = useCallback((payload) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify(payload));
            return true;
        }
        return false;
    }, []);

    return { status, send, reconnect: connect };
}
